import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useReactMediaRecorder } from "react-media-recorder";
import { get, set, del } from "idb-keyval";

import { client, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { get_csrf_token } from "../../core/auth";
import { postNode } from "../../core/postNode";

import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  IconButton,
  TextField,
  Snackbar,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";

import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopIcon from "@mui/icons-material/Stop";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";

const STORAGE_KEY = "recordedVideo";
const MAX_DURATION_SECONDS = 30;

const RECORDER_OPTIONS = { videoBitsPerSecond: 1_000_000 };

const LivePreview = ({ stream }) => {
  const videoRef = useRef(null);

  const trackKey = stream
    ? stream
        .getTracks()
        .map((t) => t.id)
        .join(",")
    : "";

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackKey]);

  if (!stream) return null;
  return (
    <video
      className="video-recorder-live-preview"
      ref={videoRef}
      autoPlay
      muted
      playsInline
    />
  );
};

const ElapsedTimer = ({ status, maxSeconds, onLimit, idleLabel }) => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const accumulatedRef = useRef(0); // ms from earlier recording segments
  const segmentStartRef = useRef(null); // start of the current segment
  const onLimitRef = useRef(onLimit);

  useEffect(() => {
    onLimitRef.current = onLimit;
  }, [onLimit]);

  useEffect(() => {
    if (status === "recording") {
      segmentStartRef.current = Date.now();

      const id = setInterval(() => {
        const total =
          accumulatedRef.current + (Date.now() - segmentStartRef.current);
        setElapsedMs(total);
        if (total >= maxSeconds * 1000) {
          clearInterval(id);
          if (onLimitRef.current) onLimitRef.current();
        }
      }, 250);

      // Runs on pause, stop, or unmount: bank the time from this segment
      return () => {
        clearInterval(id);
        accumulatedRef.current += Date.now() - segmentStartRef.current;
      };
    }

    // Keep the count while paused / stopping; reset for anything else
    if (status !== "paused" && status !== "stopping") {
      accumulatedRef.current = 0;
      setElapsedMs(0);
    }
    return undefined;
  }, [status, maxSeconds]);

  const seconds = Math.min(Math.floor(elapsedMs / 1000), maxSeconds);

  if (status === "recording") {
    return (
      <Alert variant="outlined" severity="warning" className="video-recorder-status-wrapper">
        <Typography variant="body2" color="text.secondary">
          {`Recording… ${seconds}s / ${maxSeconds}s`}
        </Typography>
      </Alert>
    );
  }
  if (status === "paused") {
    return (
      <Alert variant="outlined" severity="warning" className="video-recorder-status-wrapper">
        <Typography variant="body2" color="text.secondary">
          {`Paused ${seconds}s / ${maxSeconds}s`}
        </Typography>
      </Alert>
    );
  }
  return (
    <Alert variant="outlined" severity="info" className="video-recorder-status-wrapper">
      <Typography
        className="video-recorder-timer"
        variant="body2"
        color="text.secondary"
      >
        {idleLabel}
      </Typography>
    </Alert>
  );
};

const VideoRecorder = forwardRef(
  ({ onSaved, onUploaded, onDirtyChange }, ref) => {
    const user = useSelector((state) => state.authReducer.user);

    const [savedBlob, setSavedBlob] = useState(null);
    const [savedUrl, setSavedUrl] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [recordingName, setRecordingName] = useState("");
    const [csrf_token, setCSRFToken] = useState(user.csrf_token);

    const [videoError, setVideoError] = useState(null);
    const [videoUploading, setVideoUploading] = useState(false);

    // Load any previously saved recording on mount
    useEffect(() => {
      let cancelled = false;
      get(STORAGE_KEY)
        .then((blob) => {
          if (!cancelled && blob) setSavedBlob(blob);
        })
        .catch((e) => console.error("Could not read saved recording", e));
      return () => {
        cancelled = true;
      };
    }, []);

    // Create / revoke an object URL whenever the saved blob changes
    useEffect(() => {
      if (!savedBlob) {
        setSavedUrl(null);
        return undefined;
      }
      const url = URL.createObjectURL(savedBlob);
      setSavedUrl(url);
      return () => URL.revokeObjectURL(url);
    }, [savedBlob]);

    const handleStop = useCallback(
      async (blobUrl, blob) => {
        try {
          await set(STORAGE_KEY, blob);
          setSavedBlob(blob);
          setSaveError(null);
          if (onSaved) onSaved(blob);
        } catch (e) {
          console.error("Could not save recording", e);
          setSaveError("Could not save the recording in the browser.");
        }
      },
      [onSaved],
    );

    const VIDEO_CONSTRAINTS = {
      aspectRatio: { ideal: 16 / 9 },
      width: { ideal: 1280 },
      height: { ideal: 720 },
    };

    const {
      status,
      error,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      previewStream,
      clearBlobUrl,
    } = useReactMediaRecorder({
      video: VIDEO_CONSTRAINTS,
      audio: true,
      askPermissionOnMount: true,
      mediaRecorderOptions: RECORDER_OPTIONS,
      onStop: handleStop,
    });

    const isRecording = status === "recording";
    const isPaused = status === "paused";
    const isDirty = isRecording || isPaused || Boolean(savedBlob);

    // Ref keeps this effect from re-firing if the parent passes an inline function
    const onDirtyChangeRef = useRef(onDirtyChange);
    useEffect(() => {
      onDirtyChangeRef.current = onDirtyChange;
    });

    useEffect(() => {
      if (onDirtyChangeRef.current) onDirtyChangeRef.current(isDirty);
    }, [isDirty]);

    const handleDiscard = async () => {
      try {
        await del(STORAGE_KEY);
      } catch (e) {
        console.error("Could not clear saved recording", e);
      }
      setSavedBlob(null);
      setSaveError(null);
      clearBlobUrl();
    };

    const actionsContainer = () => {
      return (
        <Row className="video-recorder-actions-container">
          {!(isRecording || isPaused) && (
            <>
              <Col className="d-flex justify-content-center">
                <IconButton
                  className="video-recorder-actions-btn record"
                  onClick={async () => {
                    if (savedBlob) await handleDiscard();
                    if (recordingName) setRecordingName("");
                    startRecording();
                  }}
                >
                  <VideocamIcon />
                  <Typography owner="admin" variant="button" lh="base">
                    {savedUrl ? "Record again" : "Start recording"}
                  </Typography>
                </IconButton>
              </Col>
              <Col className="d-flex justify-content-center">
                <IconButton
                  className="video-recorder-actions-btn upload"
                  disabled={!savedBlob || videoUploading}
                  onClick={() => uploadRecording().catch(() => {})}
                >
                  <UploadIcon />
                  <Typography owner="admin" variant="button">
                    Upload
                  </Typography>
                </IconButton>
              </Col>
            </>
          )}
          {(isRecording || isPaused) && (
            <>
              <Col className="d-flex justify-content-center">
                <IconButton
                  className="video-recorder-actions-btn pause"
                  onClick={() => {
                    if (isPaused) {
                      resumeRecording();
                    } else {
                      pauseRecording();
                    }
                  }}
                >
                  {!isPaused ? <PauseIcon /> : <PlayArrowIcon />}
                  <Typography owner="admin" variant="button">
                    {!isPaused ? "Pause" : "Resume"}
                  </Typography>
                </IconButton>
              </Col>
              <Col className="d-flex justify-content-center">
                <IconButton
                  className="video-recorder-actions-btn stop"
                  onClick={stopRecording}
                >
                  <StopIcon />
                  <Typography owner="admin" variant="button">
                    Stop
                  </Typography>
                </IconButton>
              </Col>
            </>
          )}
        </Row>
      );
    };

    useImperativeHandle(ref, () => ({
      discard: handleDiscard,
      upload: uploadRecording,
    }));

    const createMedia = (response, media_type, field, name) => {
      const filename = response.filename
        ? response.filename
        : `unknown-${Date.now()}`;
      const data = {
        bundle: media_type,
        field: field,
        fid: response.drupal_internal__fid,
        filename: filename,
        name: name && name.trim() ? name.trim() : filename,
      };

      return postNode("/media_library", data, csrf_token);
    };

    const uploadVideoFile = async (
      file,
      {
        mediaType = "video",
        field = "field_media_video_file",
        name = "",
        onProgress = () => {},
      } = {},
    ) => {
      const url = `${serverUrl}/jsonapi/media/${mediaType}/${field}`;
      let request;

      async function performUpload(csrfToken) {
        return new Promise((resolve, reject) => {
          request = new XMLHttpRequest();
          request.open("POST", url);
          request.withCredentials = true;

          request.setRequestHeader("Accept", "application/vnd.api+json");
          request.setRequestHeader("Content-Type", "application/octet-stream");
          request.setRequestHeader(
            "Content-disposition",
            `file; filename="${encodeURI(file.name)}"`,
          );
          request.setRequestHeader("X-CSRF-Token", csrfToken);

          // Progress
          request.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              onProgress({
                loaded: e.loaded,
                total: e.total,
                percent: (e.loaded / e.total) * 100,
              });
            }
          };

          request.onerror = () => reject("Network error during upload");

          request.onload = async () => {
            // SUCCESS CASE
            if (request.status >= 200 && request.status < 300) {
              try {
                const response = JSON.parse(request.responseText);

                // Create media entity (same as FilePond flow)
                const result = await createMedia(
                  response.data.attributes,
                  mediaType,
                  field,
                  name,
                );

                resolve(result);
              } catch (err) {
                reject("Failed to parse or create media");
              }
              return;
            }

            // TOKEN EXPIRED → REFRESH & RETRY
            if (
              request.status === 403 &&
              request.responseText.includes(
                "X-CSRF-Token request header is invalid",
              )
            ) {
              try {
                const newTokenRes = await get_csrf_token();

                if (newTokenRes.data) {
                  setCSRFToken(newTokenRes.data);
                  const retry = await performUpload(newTokenRes.data);
                  resolve(retry);
                } else {
                  reject("Failed to refresh CSRF token");
                }
              } catch (err) {
                reject("CSRF refresh error");
              }
              return;
            }

            // OTHER ERRORS
            reject(`Upload failed with status ${request.status}`);
          };

          request.send(file);
        });
      }

      return performUpload(csrf_token);
    };

    // const uploadVideoRecording = async (file, name) => {
    //   try {
    //     const mediaUpload = await uploadVideoFile(file, { name });

    //     if (mediaUpload?.data?.mid[0]?.value) {
    //       let path = `media_api?mid=${mediaUpload?.data?.mid[0]?.value}`;

    //       getOnlineNode(path)
    //         .then((response) => {
    //           const media = response.data.rows[0];
    //         })
    //         .catch((_error) => {
    //           setVideoUploading(false);
    //           setVideoError("Failed to attach video. Please try again.");
    //         });
    //     } else {
    //       setVideoUploading(false);
    //       setVideoError("Failed to upload video. Please try again.");
    //     }
    //   } catch (err) {
    //     setVideoUploading(false);
    //     setVideoError("Failed to upload video. Please try again.");
    //   }
    // };

    const uploadRecording = async () => {
      if (isRecording || isPaused) {
        setVideoError("Stop the recording before uploading.");
        throw new Error("Recording in progress");
      }
      if (!savedBlob) {
        setVideoError("There's no recording to upload.");
        throw new Error("No recording");
      }

      setVideoUploading(true);
      try {
        const mimeType = (savedBlob.type || "video/webm").split(";")[0];
        const extension = mimeType === "video/mp4" ? "mp4" : "webm";
        const filename = `${user.current_user.uid}-recording-${Date.now()}.${extension}`;
        const file = new File([savedBlob], filename, { type: mimeType });

        const media = await uploadVideoFile(file, { name: recordingName });
        const mid = media?.data?.mid?.[0]?.value;
        if (!mid) throw new Error("No media ID returned");

        const video = { mid, name: recordingName.trim() || filename };

        // Uploaded, so the local copy isn't needed any more
        await handleDiscard();
        setVideoUploading(false);
        if (onUploaded) onUploaded(video);
        return video;
      } catch (err) {
        setVideoUploading(false);
        setVideoError("Failed to upload video. Please try again.");
        throw err;
      }
    };

    return (
      <Stack spacing={2} className="video-recorder-container">
        <ElapsedTimer
          status={status}
          maxSeconds={MAX_DURATION_SECONDS}
          onLimit={stopRecording}
          idleLabel={savedUrl ? "Saved locally" : "Ready to record"}
        />
        <TextField
          fullWidth
          size="small"
          label="Recording name"
          placeholder="Name your recording"
          className="video-recorder-name-input"
          value={recordingName}
          disabled={videoUploading}
          onChange={(e) => setRecordingName(e.target.value)}
        />
        {error && error !== "none" && (
          <Alert severity="error">Camera error: {error}</Alert>
        )}
        {saveError && <Alert severity="warning">{saveError}</Alert>}

        {/* Live camera while not reviewing a saved clip */}
        {!savedUrl && <LivePreview stream={previewStream} />}

        {/* Playback of the saved clip */}
        {savedUrl && (
          <video
            className="video-recorder-playback"
            src={savedUrl}
            controls
            playsInline
          />
        )}

        <Box className="w-100">{actionsContainer()}</Box>

        <Snackbar
          open={videoError ? true : false}
          autoHideDuration={3000}
          onClose={() => {
            setVideoError(null);
          }}
          message={videoError}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => {
                setVideoError(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Stack>
    );
  },
);

VideoRecorder.displayName = "VideoRecorder";

export default VideoRecorder;
